// Veřejná stránka testu školení: /skoleni/:token (10.8.2026).
//
// Vzor: KandidatTest.jsx (vstupní test kandidátů). Rozdíly: časovač (limit
// z backendu, typicky 10 minut), otázky přijdou až po kliknutí na Spustit
// (server tím startuje odpočet), auto-odeslání při vypršení času a výsledek
// se vyplňujícímu ukáže hned (interní školení).
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, Clock, GraduationCap } from "lucide-react";

function fmtCas(sec) {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function SkoleniTest() {
  const { token } = useParams();
  const [stav, setStav] = useState("load"); // load | intro | test | hotovo | jiz_hotovo | chyba
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState(null);      // { jmeno, nazev, limitMin, pocetOtazek }
  const [otazky, setOtazky] = useState([]);
  const [odpovedi, setOdpovedi] = useState([]);
  const [zbyva, setZbyva] = useState(null);
  const [vysledek, setVysledek] = useState(null); // { spravne, celkem, procent, stav }
  const [odesilam, setOdesilam] = useState(false);
  // Potvrzeni pred odeslanim s nevyplnenymi otazkami. Verejna stranka nema
  // Layout (a tedy ani ConfirmHost), nativni window.confirm je mimo brand
  // (pripominka Ladi 10.8.2026) - proto vlastni mini modal primo tady.
  const [potvrditOdeslani, setPotvrditOdeslani] = useState(false);
  const odeslanoRef = useRef(false);

  const api = `/api/hr-skoleni-test?token=${encodeURIComponent(token || "")}`;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(api);
        const j = await r.json();
        if (!r.ok || !j.ok) { setChyba(j.error || "Neznámá chyba"); setStav("chyba"); return; }
        setInfo(j);
        if (j.hotovo) { setStav("jiz_hotovo"); return; }
        if (j.zapocato) {
          // Test uz bezi (odchod ze stranky, refresh, jine zarizeni): zadne
          // intro, rovnou pokracovat. Cas drzi server od prvniho spusteni.
          await spustit();
          return;
        }
        setStav("intro");
      } catch {
        setChyba("Zkontroluj připojení a zkus odkaz otevřít znovu.");
        setStav("chyba");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Odpočet — tiká lokálně, server má vlastní kontrolu (+45 s tolerance).
  useEffect(() => {
    if (stav !== "test" || zbyva == null) return undefined;
    const t = setInterval(() => setZbyva((z) => (z > 0 ? z - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [stav, zbyva == null]);

  // Auto-odeslání při vypršení.
  useEffect(() => {
    if (stav === "test" && zbyva === 0 && !odeslanoRef.current) odesli(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zbyva, stav]);

  async function spustit() {
    setOdesilam(true);
    try {
      const r = await fetch(api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "start" }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) { setChyba(j.error || "Test se nepodařilo spustit"); setStav("chyba"); return; }
      setOtazky(j.otazky || []);
      setOdpovedi(new Array((j.otazky || []).length).fill(null));
      setZbyva(j.zbyvaSec);
      setStav("test");
    } catch {
      setChyba("Test se nepodařilo spustit, zkus to znovu.");
    } finally {
      setOdesilam(false);
    }
  }

  async function odesli(auto = false) {
    if (odeslanoRef.current) return;
    if (!auto && odpovedi.some((o) => o == null) && !potvrditOdeslani) {
      setPotvrditOdeslani(true);
      return;
    }
    setPotvrditOdeslani(false);
    odeslanoRef.current = true;
    setOdesilam(true);
    try {
      const r = await fetch(api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ odpovedi }),
      });
      const j = await r.json();
      if (r.status === 409) { setStav("jiz_hotovo"); return; }
      if (!r.ok || !j.ok) { odeslanoRef.current = false; setChyba(j.error || "Odeslání selhalo"); return; }
      setVysledek(j);
      setStav("hotovo");
    } catch {
      odeslanoRef.current = false;
      setChyba("Odeslání selhalo, zkus to znovu.");
    } finally {
      setOdesilam(false);
    }
  }

  const zodpovezeno = odpovedi.filter((o) => o != null).length;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 text-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <GraduationCap className="w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <div className="font-bold truncate">{info?.nazev || "Školení FitWay40"}</div>
              <div className="text-[11px] text-white/70">Interní test FitWay40</div>
            </div>
          </div>
          {stav === "test" && zbyva != null && (
            <div className={`flex items-center gap-1.5 font-mono text-lg font-bold px-3 py-1 rounded-xl ${zbyva <= 60 ? "bg-rose-500/90 animate-pulse" : "bg-white/15"}`}>
              <Clock className="w-4 h-4" /> {fmtCas(zbyva)}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {stav === "load" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Načítám test…
          </div>
        )}

        {stav === "chyba" && (
          <div className="bg-white border border-rose-200 rounded-2xl p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-slate-800">Test se nepodařilo otevřít</div>
              <p className="text-sm text-slate-600 mt-1">{chyba}</p>
              <p className="text-sm text-slate-500 mt-3">Ozvi se prosím tomu, kdo ti odkaz poslal.</p>
            </div>
          </div>
        )}

        {stav === "jiz_hotovo" && (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <div className="text-lg font-bold text-slate-800">Test už máš odeslaný</div>
            <p className="text-sm text-slate-600 mt-1">Každý test se vyplňuje jen jednou. Tady vidíš svůj výsledek.</p>
            {info?.vysledek && (
              <>
                <div className="mt-4 text-4xl font-black text-slate-800">
                  {info.vysledek.spravne}<span className="text-slate-400 text-2xl">/{info.vysledek.celkem}</span>
                </div>
                <div className="mt-1 font-bold text-slate-600">{info.vysledek.procent} % správně</div>
              </>
            )}
          </div>
        )}

        {stav === "hotovo" && vysledek && (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <div className="text-lg font-bold text-slate-800">{info?.jmeno ? `Díky, ${info.jmeno}!` : "Díky!"}</div>
            {vysledek.stav === "vyprselo" && (
              <p className="text-sm text-amber-700 mt-2">Čas vypršel. Započítaly se odpovědi vyplněné do limitu.</p>
            )}
            <div className="mt-4 text-4xl font-black text-slate-800">
              {vysledek.spravne}<span className="text-slate-400 text-2xl">/{vysledek.celkem}</span>
            </div>
            <div className={`mt-1 font-bold ${vysledek.procent >= 75 ? "text-emerald-600" : vysledek.procent >= 50 ? "text-amber-600" : "text-rose-600"}`}>
              {vysledek.procent} % správně
            </div>
            {vysledek.zaverecnyToken && (
              <div className="mt-6 bg-brand-50 border border-brand-200 rounded-xl p-4 text-sm text-brand-900">
                Máš hotové testy ke všem modulům, odemkl se ti závěrečný test.
                <a href={`/skoleni/${vysledek.zaverecnyToken}`} className="btn btn-primary mt-3 w-full">Přejít na závěrečný test</a>
              </div>
            )}
          </div>
        )}

        {stav === "intro" && info && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <div className="text-lg font-bold text-slate-800">{info.jmeno ? `Ahoj ${info.jmeno},` : "Ahoj,"}</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              čeká tě krátký test ze školení <b>{info.nazev}</b>. Má <b>{info.pocetOtazek} otázek</b>,
              u každé je právě jedna správná odpověď.
            </p>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex items-start gap-2.5">
              <Clock className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                Na vyplnění máš <b>{info.limitMin} minut</b> (zhruba 30 sekund na otázku). Čas začne běžet, jakmile klikneš na Spustit test,
                a běží i při odchodu ze stránky. Test jde odeslat jen jednou, tak si na něj najdi klidnou chvíli.
              </div>
            </div>
            <button
              onClick={spustit}
              disabled={odesilam}
              className="btn btn-primary mt-5 w-full"
            >
              {odesilam ? "Spouštím…" : "Spustit test"}
            </button>
          </div>
        )}

        {stav === "test" && (
          <div className="space-y-4">
            {otazky.map((o, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="font-semibold text-slate-800 text-sm">
                  <span className="text-slate-400 mr-1.5">{i + 1}.</span>{o.ot}
                </div>
                <div className="mt-3 space-y-1.5">
                  {o.moznosti.map((m, mi) => (
                    <label
                      key={mi}
                      className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer text-sm transition-colors ${odpovedi[i] === mi ? "border-brand-400 bg-brand-50" : "border-slate-200 hover:bg-slate-50"}`}
                    >
                      <input
                        type="radio"
                        name={`ot-${i}`}
                        checked={odpovedi[i] === mi}
                        onChange={() => setOdpovedi((prev) => { const n = [...prev]; n[i] = mi; return n; })}
                        className="mt-0.5 accent-brand-600"
                      />
                      <span className="text-slate-700">{m}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {potvrditOdeslani && (
              <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setPotvrditOdeslani(false)}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                  <div className="font-bold text-slate-800 text-lg">Odeslat nedokončený test?</div>
                  <p className="text-sm text-slate-600 mt-2">
                    Zodpovězeno máš {zodpovezeno} z {otazky.length} otázek. Nevyplněné se počítají jako špatně a test už nejde otevřít znovu.
                  </p>
                  <div className="flex gap-2.5 mt-5">
                    <button onClick={() => setPotvrditOdeslani(false)} className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50">
                      Zpět k testu
                    </button>
                    <button onClick={() => odesli(true)} className="btn btn-primary flex-1">
                      Odeslat
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="sticky bottom-3">
              <button
                onClick={() => odesli(false)}
                disabled={odesilam}
                className="btn btn-primary w-full"
              >
                {odesilam ? "Odesílám…" : `Odeslat test (${zodpovezeno}/${otazky.length} zodpovězeno)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
